// src/pages/personal/analytics/FunnelSection/components/FunnelChart.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/utils/Formatters';
import { FunnelStageData, FunnelData } from '../hooks/useFunnelData';
import styles from '../FunnelSection.module.css';
import useBusinessCurrency from '@/hooks/useBusinessCurrency';

// Простые компоненты иконок, чтобы не зависеть от внешней библиотеки
const ArrowUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5M5 12l7-7 7 7"/>
  </svg>
);

const ArrowDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12l7 7 7-7"/>
  </svg>
);

// Компонент для отображения отдельной метрики
const MetricItem: React.FC<{
  name: string;
  value: number | null;
  change?: number | null;
  format?: (value: number) => string;
  dataMissing?: boolean;
}> = ({
  name,
  value,
  change,
  format = formatNumber,
  dataMissing = false
}) => {
  const { t } = useTranslation('funnel');
  
  return (
    <div className={styles.funnelStageMetric}>
      <div className={styles.funnelStageMetricName}>
        {t(`metrics.${name}`, name)}
      </div>
      <div className={styles.funnelStageMetricValue}>
        {dataMissing || value === null ? (
          <span className="italic text-opacity-70">—</span>
        ) : (
          <>
            {format(value)}
            {change !== undefined && change !== null && (
              <span className={`ml-2 text-xs ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {change >= 0 ? '+' : ''}{Math.round(change)}%
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Компонент для отображения карточки метрики
const MetricCard: React.FC<{
  title: string;
  value: number | null;
  previousValue?: number | null;
  change?: number | null;
  formatter?: (value: number) => string;
  description?: string;
  positiveIsBetter?: boolean;
}> = ({
  title,
  value,
  previousValue,
  change,
  formatter = formatNumber,
  description,
  positiveIsBetter = true
}) => {
  // Определяем цвет изменения в зависимости от того, лучше ли положительное изменение
  const getChangeColor = (changeValue: number | null) => {
    if (changeValue === null) return 'text-gray-400';
    if (positiveIsBetter) {
      return changeValue >= 0 ? 'text-green-600' : 'text-red-600';
    } else {
      return changeValue <= 0 ? 'text-green-600' : 'text-red-600';
    }
  };

  return (
    <div className={styles.metricCard}>
      <div className={styles.metricTitle}>{title}</div>
      
      {description && (
        <div className={styles.metricDescription}>{description}</div>
      )}
      
      <div className={styles.metricValue}>
        {value === null ? (
          <span className="text-gray-400">—</span>
        ) : (
          formatter(value)
        )}
      </div>
      
      {change !== null && change !== undefined && (
        <div className={`${styles.metricChange} ${getChangeColor(change)}`}>
          {change >= 0 ? '+' : ''}{Math.round(change)}%
          {previousValue !== null && previousValue !== undefined && (
            <span className="text-gray-500 text-xs ml-1">
              от {formatter(previousValue)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Компонент для отображения этапа воронки
const FunnelStage: React.FC<{
  stage: FunnelStageData;
  showConversion?: boolean;
}> = ({
  stage,
  showConversion = true
}) => {
  const { t } = useTranslation('funnel');
  const { currencySymbol } = useBusinessCurrency();
  
  // Определяем класс в зависимости от этапа
  const stageClass = stage.stage === 'retention' ? 
    `${styles.funnelStage} ${styles.retention}` : 
    styles.funnelStage;
  
  // Проверяем, есть ли данные
  const dataMissing = stage.data_missing === true;
  
  // Функция для форматирования процентов
  const formatPercent = (value: number): string => {
    return `${Math.round(value)}%`;
  };
  
  // Функция для форматирования валюты
  const formatCurrency = (value: number): string => {
    return `${formatNumber(value)} ${currencySymbol}`;
  };
  
  return (
    <>
      <div 
        className={stageClass}
        style={{ 
          backgroundColor: stage.color,
          color: 'white'
        }}
      >
        <div className={styles.funnelStageHeader}>
          <div className={styles.funnelStageName}>
            {t(`stages.${stage.stage}`, stage.name)}
          </div>
          <div className={styles.funnelStageValue}>
            {dataMissing || stage.value === null ? (
              <span className="italic text-opacity-80">Нет данных</span>
            ) : (
              <>
                {formatNumber(stage.value)}
                {stage.change !== 0 && stage.change !== null && (
                  <span className={styles.funnelStageChange}>
                    {stage.trend === 'up' ? (
                      <span className="ml-2 text-green-300"><ArrowUpIcon /></span>
                    ) : stage.trend === 'down' ? (
                      <span className="ml-2 text-red-300"><ArrowDownIcon /></span>
                    ) : null}
                    <span className="ml-1">
                      {stage.change > 0 ? '+' : ''}{Math.round(stage.change)}%
                    </span>
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        
        <div className={styles.funnelStageMetrics}>
          {Object.entries(stage.metrics || {}).map(([key, metric]) => (
            <MetricItem 
              key={key}
              name={key}
              value={metric.value}
              change={metric.change}
              format={
                key.includes('percentage') || 
                key.includes('rate') || 
                key.includes('ctr') ? 
                  formatPercent : 
                  key.includes('cost') || 
                  key.includes('cpm') || 
                  key.includes('cpl') || 
                  key.includes('cpc') || 
                  key.includes('cac') || 
                  key.includes('aov') ||
                  key.includes('ltv') ? 
                    formatCurrency : 
                    formatNumber
              }
              dataMissing={dataMissing}
            />
          ))}
        </div>
        
        {dataMissing && (
          <div className="w-full text-center mt-2 px-4 py-2 bg-white bg-opacity-20 rounded-md">
            <p className="text-xs">
              {t('data_hint', 'Для получения данных интегрируйте рекламные каналы')}
            </p>
          </div>
        )}
      </div>
      
      {showConversion && stage.conversion_from_previous !== undefined && stage.conversion_from_previous !== null && !dataMissing && (
        <div className={styles.funnelConversion}>
          {t('conversion', 'Конверсия')}: {Math.round(stage.conversion_from_previous)}%
        </div>
      )}
    </>
  );
};

// Компонент для отображения отсутствия данных
const NoDataAlert: React.FC = () => {
  const { t } = useTranslation('funnel');
  
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-700 p-4 mb-4 rounded">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm">{t('no_data_message', 'Не найдены данные о маркетинговых расходах для отображения воронки продаж.')}</p>
          <p className="text-sm mt-2">{t('no_data_hint', 'Для получения полной аналитики выполните настройку маркетинговых каналов и внесите данные о расходах на рекламу.')}</p>
        </div>
      </div>
    </div>
  );
};

// Основной компонент воронки
const FunnelChart: React.FC<{
  data: FunnelStageData[];
  additionalMetrics?: FunnelData['additional_metrics'];
}> = ({ data, additionalMetrics }) => {
  const { t } = useTranslation('funnel');
  useBusinessCurrency();
  
  // Функция для форматирования коэффициента
  const formatRatio = (value: number): string => {
    return value.toFixed(2);
  };
  
  // Функция для форматирования дней
  const formatDays = (value: number): string => {
    return `${Math.round(value)} дн.`;
  };
  
  // Функция для форматирования процентов
  const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };
  
  // Функция для форматирования частоты
  const formatFrequency = (value: number): string => {
    return `${value.toFixed(1)} / мес.`;
  };
  
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        {t('no_data', 'Нет данных для отображения')}
      </div>
    );
  }
  
  // Проверяем, есть ли реальные данные или все этапы помечены как data_missing
  const allDataMissing = data.every(stage => stage.data_missing === true);
  
  // Функция для рендеринга дополнительных метрик
  const renderAdditionalMetrics = () => {
    if (!additionalMetrics || Object.keys(additionalMetrics).length === 0) return null;
    
    return (
      <>
        {/*<h2 className={styles.funnelTitle + ' mt-6'}>{t('additional_metrics.title', 'Дополнительные метрики')}</h2>
        <p className="text-gray-600 mb-4">{t('additional_metrics.description', 'Аналитические показатели эффективности')}</p>*/}
        <div className={styles.additionalMetricsContainer}>
          {additionalMetrics.ltv_cac_ratio && (
            <MetricCard
              title={t('metrics.ltv_cac_ratio', 'LTV:CAC')}
              value={additionalMetrics.ltv_cac_ratio.value}
              previousValue={additionalMetrics.ltv_cac_ratio.previous_value}
              change={additionalMetrics.ltv_cac_ratio.change}
              formatter={formatRatio}
              description={t('metrics.ltv_cac_ratio_desc', 'Соотношение ценности клиента к стоимости привлечения')}
            />
          )}
          
          {additionalMetrics.time_to_first_purchase && (
            <MetricCard
              title={t('metrics.time_to_first_purchase', 'Время до покупки')}
              value={additionalMetrics.time_to_first_purchase.value}
              previousValue={additionalMetrics.time_to_first_purchase.previous_value}
              change={additionalMetrics.time_to_first_purchase.change}
              formatter={formatDays}
              description={t('metrics.time_to_first_purchase_desc', 'Среднее время от первого контакта до покупки')}
              positiveIsBetter={false} // Меньше дней - лучше
            />
          )}
          
          {additionalMetrics.purchase_frequency && (
            <MetricCard
              title={t('metrics.purchase_frequency', 'Частота покупок')}
              value={additionalMetrics.purchase_frequency.value}
              previousValue={additionalMetrics.purchase_frequency.previous_value}
              change={additionalMetrics.purchase_frequency.change}
              formatter={formatFrequency}
              description={t('metrics.purchase_frequency_desc', 'Число заказов в месяц между первой и последней покупкой')}
            />
          )}
          
          {additionalMetrics.romi && (
            <MetricCard
              title={t('metrics.romi', 'ROMI')}
              value={additionalMetrics.romi.value}
              previousValue={additionalMetrics.romi.previous_value}
              change={additionalMetrics.romi.change}
              formatter={formatPercent}
              description={t('metrics.romi_desc', 'Доходность маркетинговых инвестиций с учетом маржинальности')}
            />
          )}
        </div>
      </>
    );
  };
  
  return (
    <div className={styles.funnelContainer}>
      <h2 className={styles.funnelTitle}>{t('chart.title', 'Воронка продаж')}</h2>
      <div className={styles.funnelDescription}>
        {t('chart.description', 'Анализ конверсии по этапам')}
      </div>
      
      {allDataMissing && <NoDataAlert />}
      
      <div className={styles.funnelStageContainer}>
        {data.map((stage, index) => (
          <FunnelStage 
            key={stage.stage} 
            stage={stage}
            showConversion={index > 0}
          />
        ))}
      </div>
      
      {/* Добавляем секцию дополнительных метрик */}
      {renderAdditionalMetrics()}
    </div>
  );
};

export default FunnelChart;